import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { ClientVisitStatus } from '../../enums/client-visit-status.enum';
import { BaseEntity } from './base.entity';
import { ClientAddressEntity } from './client-address.entity';
import { ClientEntity } from './client.entity';
import { SalesTripEntity } from './sales-trip.entity';

/**
 * Outcome-only stop status (decision 16 — no arrived / in-progress):
 *   planned     — on the plan, nothing recorded yet
 *   visited     — a visit row exists and is linked (`clientVisitId`). The rep
 *                 was there, including a visit that found the account closed
 *                 or no one available (PD-16957: an attempted visit)
 *   skipped     — the rep did not go (a reason is required)
 *   rescheduled — moved to a stop on another trip (`rescheduledToStopId`)
 *
 * PD-16957 — there used to be a fifth status, `missed` ("went, or meant to,
 * and could not complete it"). It overlapped both a skip (did not get there)
 * and a visit that found no one, so a rep could not tell which to pick. It
 * is gone: not going is a skip, and going and finding the account closed is
 * a visit with that outcome. Migration
 * 1791688562669-ConvertMissedSalesTripStopsToSkipped turns any row left with
 * it into `skipped`.
 */
export type SalesTripStopStatus =
	| 'planned'
	| 'visited'
	| 'skipped'
	| 'rescheduled';

/**
 * Every reason a skipped stop may carry, stored as a varchar so new reasons
 * need no enum migration. They began as a mirror of `trip_commit_skip.reason`.
 *
 * Not all of them are offered any more (see `SALES_TRIP_SKIP_REASONS`); the
 * rest stay here so a stop skipped with one before PD-16957 still reads in
 * words.
 */
export const SALES_TRIP_STOP_OUTCOME_REASONS = [
	'not_available',
	'closed',
	'ran_out_of_time',
	'customer_request',
	'other',
] as const;
export type SalesTripStopOutcomeReason =
	(typeof SALES_TRIP_STOP_OUTCOME_REASONS)[number];

/**
 * How a reason reads on screen. The stored codes were shown with the
 * underscores swapped for spaces ("ran out of time"), lower case in a picker
 * and a sentence alike. A code this list does not know (a newer one) still
 * reads that way.
 */
export const SALES_TRIP_STOP_OUTCOME_REASON_LABELS: Record<
	SalesTripStopOutcomeReason,
	string
> = {
	not_available: 'Not available',
	closed: 'Closed',
	ran_out_of_time: 'Ran out of time',
	customer_request: 'Customer request',
	other: 'Other',
};

/**
 * PD-16957 — the reasons offered for skipping a stop, and the only ones the
 * server takes for a new skip. A skip means the rep did not go, so "Closed"
 * and "Not available" are no longer reasons to skip: finding the account
 * closed, or no one there, is a visit with that outcome (an attempted visit,
 * see `visitStatusForSalesTripOutcome`). "Other" needs a note saying what.
 */
export const SALES_TRIP_SKIP_REASONS = [
	'ran_out_of_time',
	'customer_request',
	'other',
] as const satisfies ReadonlyArray<SalesTripStopOutcomeReason>;
export type SalesTripSkipReason = (typeof SALES_TRIP_SKIP_REASONS)[number];

export function isSalesTripSkipReason(
	value: unknown,
): value is SalesTripSkipReason {
	return (
		typeof value === 'string' &&
		(SALES_TRIP_SKIP_REASONS as ReadonlyArray<string>).includes(value)
	);
}

/** PD-16957 — "Other" says nothing on its own, so a skip for it needs a note. */
export function salesTripSkipReasonNeedsDetail(
	reason: SalesTripSkipReason,
): boolean {
	return reason === 'other';
}

/**
 * PD-16957 — what came of a logged visit, picked as one chip in Log visit.
 *
 * Stored in the stop's `outcomeReason` while the stop is `visited`; a skipped
 * stop keeps one of the reasons above there instead, and logging a visit
 * replaces it. A list of its own rather than more skip reasons because the
 * two answer different questions ("what happened when I was there" and "why
 * I did not get there"), and each status must accept only its own codes: the
 * outcome endpoint would otherwise take "Order placed" as a reason for a
 * skipped stop. A varchar, like the reasons, so a new outcome needs no
 * migration. `closed` is in both lists and reads the same in both.
 *
 * Deliberately NOT written to the visit row's `outcome` column
 * (`m_sm_client_visit`): the legacy CRM reads that table by named columns,
 * and that column holds the tenant's own Log Visit catalog names
 * (PD-16801), not these codes. What the outcome does decide on the visit
 * row is its status (`visitStatusForSalesTripOutcome`).
 */
export const SALES_TRIP_VISIT_OUTCOMES = [
	'spoke_with_buyer',
	'left_info',
	'order_placed',
	'not_interested',
	'closed',
	'no_one_available',
] as const;
export type SalesTripVisitOutcome = (typeof SALES_TRIP_VISIT_OUTCOMES)[number];

export const SALES_TRIP_VISIT_OUTCOME_LABELS: Record<
	SalesTripVisitOutcome,
	string
> = {
	spoke_with_buyer: 'Spoke with buyer',
	left_info: 'Left info',
	order_placed: 'Order placed',
	not_interested: 'Not interested',
	closed: 'Closed',
	no_one_available: 'No one available',
};

export function isSalesTripVisitOutcome(
	value: unknown,
): value is SalesTripVisitOutcome {
	return (
		typeof value === 'string' &&
		(SALES_TRIP_VISIT_OUTCOMES as ReadonlyArray<string>).includes(value)
	);
}

/**
 * PD-16957 — the visit outcomes that mean the rep got there and saw no one:
 * the account was closed, or no one was available. Such a visit is an
 * attempt, not a contact.
 */
export const SALES_TRIP_NO_CONTACT_OUTCOMES = [
	'closed',
	'no_one_available',
] as const satisfies ReadonlyArray<SalesTripVisitOutcome>;

export function isSalesTripNoContactOutcome(value: unknown): boolean {
	return (
		typeof value === 'string' &&
		(SALES_TRIP_NO_CONTACT_OUTCOMES as ReadonlyArray<string>).includes(value)
	);
}

/**
 * PD-16957 (owner decision 2026-10-10) — the status of the visit row that a
 * visit logged at a stop is saved with. A visit that found the account
 * closed or no one available is `attempted`: the stop is still visited (the
 * rep was there), but the attempt is not counted as a visit, so it does not
 * move the account's last-visit or last-contact date (the account stays
 * due) and no report counts it. Any other outcome, or none (a note only), is
 * a visit that happened. Logging a visit at a stop and changing its outcome
 * afterwards both go through here, so the rule lives in one place.
 */
export function visitStatusForSalesTripOutcome(
	outcome: SalesTripVisitOutcome | null | undefined,
): ClientVisitStatus {
	return isSalesTripNoContactOutcome(outcome)
		? ClientVisitStatus.Attempted
		: ClientVisitStatus.Completed;
}

/**
 * PD-16957 — "Follow up in N days" when logging a visit: a task for the rep on
 * the account, due that many days after the visit.
 */
export const SALES_TRIP_FOLLOW_UP_DAYS = [1, 3, 7, 14] as const;
export type SalesTripFollowUpDays = (typeof SALES_TRIP_FOLLOW_UP_DAYS)[number];

export function isSalesTripFollowUpDays(
	value: unknown,
): value is SalesTripFollowUpDays {
	return (
		typeof value === 'number' &&
		(SALES_TRIP_FOLLOW_UP_DAYS as ReadonlyArray<number>).includes(value)
	);
}

/**
 * How a stored `outcomeReason` reads on screen, whichever list it came from:
 * a skip reason (one no longer offered included), or (PD-16957) a visit
 * outcome.
 */
export function salesTripStopOutcomeReasonLabel(reason: string): string {
	return (
		(SALES_TRIP_STOP_OUTCOME_REASON_LABELS as Record<string, string>)[reason] ??
		(SALES_TRIP_VISIT_OUTCOME_LABELS as Record<string, string>)[reason] ??
		reason.replace(/_/g, ' ')
	);
}

/**
 * One client on one sales trip (PD-15920). Times follow the trip's timezone
 * model: `plannedStart` is UTC, rendered in `sales_trip.timezone`.
 *
 * Timing model (decisions 14, 23): `isPinned` = a committed appointment the
 * optimizer and recalculation never move; a fluid stop's start is computed
 * from the previous stop's end + drive time. Drag / duration edits mark
 * `timesStale`; "Recalculate times" and "Optimize this day" clear it.
 */
@Entity('sales_trip_stop')
@Index('ix_sales_trip_stop_trip_sequence', ['salesTripId', 'sequence'])
@Index('ix_sales_trip_stop_client', ['clientId'])
export class SalesTripStopEntity extends BaseEntity {
	@Column({ name: 'fk_sales_trip__id', type: 'int', unsigned: true })
	public salesTripId!: number;

	@Column({ name: 'fk_client__id', type: 'int', unsigned: true })
	public clientId!: number;

	/** Defaults to the client's shipping address when null. */
	@Column({
		name: 'fk_client_address__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public clientAddressId?: number | null;

	/** Provenance when the trip was built from a route. */
	@Column({
		name: 'fk_sales_route_client__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public salesRouteClientId?: number | null;

	@Column({ name: 'sequence', type: 'int' })
	public sequence!: number;

	/** UTC. Null until the chain has been computed. */
	@Column({ name: 'plannedStart', type: 'datetime', nullable: true })
	public plannedStart?: Date | null;

	/**
	 * Copied at creation from `sales_route_client.stopDurationMinutes` or the
	 * tenant's sales default (30); editable per stop in 15-minute steps.
	 */
	@Column({ name: 'plannedDurationMinutes', type: 'int', default: 30 })
	public plannedDurationMinutes!: number;

	/**
	 * PD-16165 — the IANA zone of where this stop IS, derived from its
	 * coordinates by the server on the first recalculation after the stop is
	 * written (null until then, and for a stop with no map position). Its
	 * times are read and pinned in this zone rather than the trip's, so a day
	 * that crosses a zone line shows each stop in its own local time.
	 */
	@Column({ name: 'timezone', type: 'varchar', length: 64, nullable: true })
	public timezone?: string | null;

	@Column({ name: 'timesStale', type: 'boolean', default: false })
	public timesStale!: boolean;

	@Column({ name: 'isPinned', type: 'boolean', default: false })
	public isPinned!: boolean;

	@Column({
		name: 'estimatedDriveMinutesFromPrev',
		type: 'int',
		nullable: true,
	})
	public estimatedDriveMinutesFromPrev?: number | null;

	@Column({ name: 'status', type: 'varchar', length: 20, default: "'planned'" })
	public status!: SalesTripStopStatus;

	/**
	 * A skipped stop: one of `SALES_TRIP_STOP_OUTCOME_REASONS` (for a new skip,
	 * one of `SALES_TRIP_SKIP_REASONS`). A visited stop (PD-16957): one of
	 * `SALES_TRIP_VISIT_OUTCOMES`, or null when the rep logged only a note.
	 */
	@Column({
		name: 'outcomeReason',
		type: 'varchar',
		length: 64,
		nullable: true,
	})
	public outcomeReason?: string | null;

	@Column({ name: 'outcomeDetail', type: 'text', nullable: true })
	public outcomeDetail?: string | null;

	/** Set when status = visited; the visit row carries `fk_sales_trip_stop__id` back. */
	@Column({
		name: 'fk_client_visit__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public clientVisitId?: number | null;

	/** The stop created on a later trip when this one was rescheduled. */
	@Column({
		name: 'fk_sales_trip_stop__id_rescheduled_to',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public rescheduledToStopId?: number | null;

	@Column({ name: 'notes', type: 'text', nullable: true })
	public notes?: string | null;

	/**
	 * PD-16859 — "Reason for this visit": why the account is on the day and
	 * the background a rep needs going in. Written from the account's data
	 * when the stop is created (and the rep's focus, when the day was planned
	 * around one); editable by hand. Notes stay the rep's own.
	 */
	@Column({ name: 'visitReason', type: 'varchar', length: 500, nullable: true })
	public visitReason?: string | null;

	@ManyToOne(() => SalesTripEntity, (trip) => trip.stops)
	@JoinColumn({ name: 'fk_sales_trip__id' })
	public fkSalesTrip!: SalesTripEntity;

	// Optional in TS because the relation is only present when explicitly
	// loaded (and is null when the client has been soft-deleted).
	@ManyToOne(() => ClientEntity)
	@JoinColumn({ name: 'fk_client__id' })
	public fkClient?: ClientEntity | null;

	@ManyToOne(() => ClientAddressEntity, { nullable: true })
	@JoinColumn({ name: 'fk_client_address__id' })
	public fkClientAddress?: ClientAddressEntity | null;
}
